import React, { createContext, useContext, useState, useRef } from 'react';
import { analyzeProfile, analyzeSection } from '../api/aiAdvisorService';
import { getSpeechAudio } from '../utils/elevenLabsTTS';
import { getUser } from '../api/userDataService';
import { getWorkExperienceData } from '../api/workExperienceService';
import { getAllTransferableSkills } from '../api/transferableSkillService';
import { getBioVideo, getTopLevelVideos } from '../api/mediaDataService';
import { getQualifications } from '../api/courseDataService';
import { getUserQualifications } from '../api/existingQualificationsDataService';

const AIAdvisorContext = createContext();

export const useAIAdvisor = () => {
  const context = useContext(AIAdvisorContext);
  if (!context) {
    throw new Error('useAIAdvisor must be used within AIAdvisorProvider');
  }
  return context;
};

export const AIAdvisorProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef(null);

  // Section-specific advice state
  const [expandedSection, setExpandedSection] = useState(null);
  const [sectionAdvice, setSectionAdvice] = useState({});
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sectionAudioLoading, setSectionAudioLoading] = useState(false);
  const [sectionAudioPlaying, setSectionAudioPlaying] = useState(null);
  const sectionAudioRef = useRef(null);

  // Store complete user data for section analysis
  const completeUserDataRef = useRef(null);

  const openAdvisor = () => {
    setIsOpen(true);
    setIsMinimized(false);
  };

  const closeAdvisor = () => {
    setIsOpen(false);
    setIsMinimized(false);
    // Stop audio when closing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
    }
  };

  const minimizeAdvisor = () => {
    setIsMinimized(true);
    // Keep audio playing when minimized
  };

  const maximizeAdvisor = () => {
    setIsMinimized(false);
  };

  const getAdvice = async () => {
    setLoading(true);
    setError('');
    setAnalysis(null);
    setIsPlaying(false);

    // Stop current audio if any
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    try {
      // Get current user data from frontend service
      const userResponse = await getUser();

      // Extract user data (it's nested under result.user in API response)
      const userData = userResponse?.result?.user || userResponse;

      // Fetch additional data from separate endpoints
      let workExperience = [];
      let transferableSkills = [];
      let bioVideo = null;
      let topLevelVideos = [];
      let courseData = [];

      try {
        const workExpData = await getWorkExperienceData();
        // workExpData can be array or object with workExperience/data/result property
        workExperience = Array.isArray(workExpData)
          ? workExpData
          : (workExpData?.workExperience || workExpData?.logs || workExpData?.data || workExpData?.result || []);
      } catch (err) {
        console.error('[AI Advisor] Could not fetch work experience:', err);
      }

      try {
        const skillsData = await getAllTransferableSkills();
        // Filter for enabled skills
        transferableSkills = Array.isArray(skillsData) ? skillsData.filter(s => s.isEnable !== false) : [];
      } catch (err) {
        console.error('[AI Advisor] Could not fetch transferable skills:', err);
      }

      try {
        bioVideo = await getBioVideo();
      } catch (err) {
        console.warn('[AI Advisor] Could not fetch bio video:', err.message);
      }

      try {
        topLevelVideos = await getTopLevelVideos();
      } catch (err) {
        console.warn('[AI Advisor] Could not fetch top level videos:', err.message);
      }

      try {
        const coursesResponse = await getQualifications();
        courseData = coursesResponse?.result?.courseData || coursesResponse?.result || coursesResponse || [];
      } catch (err) {
        console.warn('[AI Advisor] Could not fetch course data:', err.message);
      }

      let previousQualifications = {};
      try {
        previousQualifications = await getUserQualifications();
      } catch (err) {
        console.warn('[AI Advisor] Could not fetch previous qualifications:', err.message);
      }

      // Combine all data for comprehensive analysis
      const completeUserData = {
        ...userData,
        workExperience: workExperience,
        transferableSkills: transferableSkills,
        bioVideo: bioVideo,
        topLevelVideos: topLevelVideos,
        courseData: courseData,
        compulsory_gcses: previousQualifications?.compulsoryGCSEs || [],
        optional_gcses: previousQualifications?.optionalGCSEs || [],
        academic_qualifications: previousQualifications?.academicQualifications || [],
        // Note: Community & Charity, Trips, Presentations are likely in customFields
      };

      // Store for section-specific analysis
      completeUserDataRef.current = completeUserData;

      // Debug: Log what we're sending to the analyser

      // Send user data to backend for analysis
      const result = await analyzeProfile(completeUserData);

      if (!result.error && result.result) {
        setAnalysis(result.result);

        // Auto-generate voice for the advice
        if (result.result.advice) {
          generateVoice(result.result.advice);
        }
      } else {
        setError(result.message || 'Failed to get AI advice');
      }
    } catch (err) {
      console.error('AI analyser error:', err);
      if (err.response?.status === 403) {
        setError('AI features are currently limited to authorized beta testers only.');
      } else {
        setError('Failed to get AI advice. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fallback: use browser Web Speech API (free, no quota)
  const playWithWebSpeech = (text) => {
    if (!window.speechSynthesis) return false;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.volume = 1.0;
    utterance.onend = () => setIsPlaying(false);
    // Store a shim on audioRef so toggleAudio works
    audioRef.current = {
      _utterance: utterance,
      _webSpeech: true,
      pause: () => { window.speechSynthesis.pause(); },
      play: () => {
        window.speechSynthesis.resume();
        return Promise.resolve();
      },
    };
    window.speechSynthesis.speak(utterance);
    setIsPlaying(true);
    return true;
  };

  const generateVoice = async (text) => {
    setAudioLoading(true);
    try {
      const audioBlob = await getSpeechAudio(text);
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audio.volume = 1.0;

      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = (err) => {
        console.error('Audio playback error:', err);
        setAudioLoading(false);
      };

      audioRef.current = audio;
      setAudioLoading(false);

      audio.play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.warn('Audio autoplay blocked — user can click Play to listen:', err);
        });

    } catch (err) {
      console.warn('ElevenLabs unavailable, falling back to Web Speech API:', err.message);
      setAudioLoading(false);
      playWithWebSpeech(text);
    }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;

    if (audioRef.current._webSpeech) {
      if (isPlaying) {
        window.speechSynthesis.pause();
        setIsPlaying(false);
      } else {
        window.speechSynthesis.resume();
        setIsPlaying(true);
      }
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Helper to extract section data from complete user data
  const getSectionData = (sectionName) => {
    if (!completeUserDataRef.current) return {};

    const userData = completeUserDataRef.current;

    // Helper to find profile field value
    const getProfileFieldValue = (title) => {
      const field = userData.profileFields?.find(f =>
        f.title === title || f.defaultTitle === title
      ) || userData.dynamicProfileFields?.find(f =>
        f.title === title || f.defaultTitle === title
      );
      return field?.value || null;
    };

    // Map section names to their data
    const sectionDataMap = {
      'Name': { name: getProfileFieldValue('Name') },
      'Profile Image': { profileImage: getProfileFieldValue('Profile Picture') || getProfileFieldValue('Profile Image') },
      'Bio': { bio: getProfileFieldValue('Bio') },
      'Bio Video': { bioVideo: userData.bioVideo },
      'Video Gallery': { videos: userData.topLevelVideos || [] },
      'Contact': { contact: getProfileFieldValue('Contact') },
      'Address': { address: getProfileFieldValue('Address') },
      'Interests': { interests: getProfileFieldValue('Interests') },
      'Work Experience': { workExperience: userData.workExperience || [] },
      'Previous Employment': { previousEmployment: getProfileFieldValue('Previous Employment') },
      'Education (Current Courses)': { courses: userData.courseData || [] },
      'Previous Qualifications': {
        compulsoryGCSEs: getProfileFieldValue('Key Stage 4 GCSE Qualification (Compulsory)'),
        optionalGCSEs: getProfileFieldValue('Key Stage 4 GCSE Qualification (Optional)'),
        otherQualifications: getProfileFieldValue('Other Academic Qualifications')
      },
      'Transferable Skills': { skills: userData.transferableSkills || [] },
      'Trips & Experiences': { trips: getProfileFieldValue('Trips & Experiences') || getProfileFieldValue('Trips, Events & Experiences') },
      'Presentation & Awards': { presentations: getProfileFieldValue('Presentation & Awards') || getProfileFieldValue('Presentations & Awards') },
      'Community & Charity': { community: getProfileFieldValue('Community / Charity Work') },
      'References': { references: getProfileFieldValue('References') },
    };

    return sectionDataMap[sectionName] || {};
  };

  const toggleSection = async (sectionName) => {
    // If clicking the same section, collapse it
    if (expandedSection === sectionName) {
      setExpandedSection(null);
      if (sectionAudioRef.current) {
        sectionAudioRef.current.pause();
        sectionAudioRef.current = null;
        setSectionAudioPlaying(null);
      }
      return;
    }

    // Expand the new section
    setExpandedSection(sectionName);

    // If we already have advice for this section, don't fetch again
    if (sectionAdvice[sectionName]) {
      return;
    }

    // Fetch section-specific advice
    setSectionLoading(true);
    try {
      // Get the actual section data
      const sectionData = getSectionData(sectionName);

      const clubInfo = completeUserDataRef.current?.club || null;
      const result = await analyzeSection(sectionName, sectionData, clubInfo);

      if (!result.error && result.result) {
        const adviceText = result.result.advice;

        setSectionAdvice(prev => ({
          ...prev,
          [sectionName]: adviceText
        }));

        // Auto-generate voice for section advice
        generateSectionVoice(sectionName, adviceText);
      } else {
        console.error('[AI Advisor] Section analyser returned error:', result.error || result);
        setSectionAdvice(prev => ({
          ...prev,
          [sectionName]: 'Sorry, I couldn\'t generate advice for this section right now. Please try again.'
        }));
      }
    } catch (err) {
      console.error('[AI Advisor] Section analyser error:', err);
      setSectionAdvice(prev => ({
        ...prev,
        [sectionName]: 'Sorry, I couldn\'t generate advice for this section right now. Please try again.'
      }));
    } finally {
      setSectionLoading(false);
    }
  };

  const playWithWebSpeechSection = (sectionName, text) => {
    if (!window.speechSynthesis) return false;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.volume = 1.0;
    utterance.onend = () => setSectionAudioPlaying(null);
    sectionAudioRef.current = {
      _utterance: utterance,
      _webSpeech: true,
      pause: () => { window.speechSynthesis.pause(); },
      play: () => { window.speechSynthesis.resume(); return Promise.resolve(); },
    };
    window.speechSynthesis.speak(utterance);
    setSectionAudioPlaying(sectionName);
    return true;
  };

  const generateSectionVoice = async (sectionName, text) => {
    setSectionAudioLoading(true);
    try {
      const audioBlob = await getSpeechAudio(text);
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audio.volume = 1.0;

      audio.onended = () => {
        setSectionAudioPlaying(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = (err) => {
        console.error('Section audio playback error:', err);
        setSectionAudioLoading(false);
      };

      sectionAudioRef.current = audio;
      setSectionAudioLoading(false);

      audio.play()
        .then(() => setSectionAudioPlaying(sectionName))
        .catch((err) => {
          console.warn('Section audio autoplay blocked — user can click Play to listen:', err);
        });
    } catch (err) {
      console.warn('ElevenLabs unavailable for section, falling back to Web Speech API:', err.message);
      setSectionAudioLoading(false);
      playWithWebSpeechSection(sectionName, text);
    }
  };

  const toggleSectionAudio = (sectionName) => {
    if (!sectionAudioRef.current) return;

    if (sectionAudioPlaying === sectionName) {
      sectionAudioRef.current.pause();
      setSectionAudioPlaying(null);
    } else {
      sectionAudioRef.current.play();
      setSectionAudioPlaying(sectionName);
    }
  };

  const value = {
    isOpen,
    isMinimized,
    analysis,
    loading,
    audioLoading,
    isPlaying,
    error,
    openAdvisor,
    closeAdvisor,
    minimizeAdvisor,
    maximizeAdvisor,
    getAdvice,
    toggleAudio,
    // Section-specific functionality
    expandedSection,
    sectionAdvice,
    sectionLoading,
    sectionAudioLoading,
    sectionAudioPlaying,
    toggleSection,
    toggleSectionAudio,
  };

  return (
    <AIAdvisorContext.Provider value={value}>
      {children}
    </AIAdvisorContext.Provider>
  );
};
